import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Terminal, ChevronLeft, ArrowRight, Loader2, Check,
} from "lucide-react";
import usePageview from "@/lib/usePageview";

const BACKEND = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");

const COUNTRIES = [
  "Australia", "New Zealand", "India", "Nigeria", "South Africa",
  "United Kingdom", "United States", "Canada", "Singapore", "Germany",
  "Netherlands", "Other",
];
const INDUSTRIES = [
  "Retail", "Hospitality", "F&B", "Consumer Goods", "Mining",
  "Banking & Finance", "Logistics", "Manufacturing", "Telecom",
  "Energy", "Healthcare", "Technology",
];

export default function ExpertsApply() {
  usePageview("/experts/apply");

  const [form, setForm] = useState({
    name: "", email: "", country: "Australia", timezone: "",
    years_experience: "", linkedin: "", industries: [], bio: "",
    desired_tier: "standard", email_consent: false,
  });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const toggleIndustry = (i) =>
    setForm((f) => ({
      ...f,
      industries: f.industries.includes(i)
        ? f.industries.filter((x) => x !== i)
        : [...f.industries, i],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.email_consent) {
      toast.error("Please tick the consent box to receive your application status by email.");
      return;
    }
    if (form.bio.trim().length < 20) {
      toast.error("Bio must be at least 20 characters — tell us about your work.");
      return;
    }
    setSubmitting(true);
    try {
      const r = await fetch(`${BACKEND}/api/analysts/apply`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          years_experience: parseInt(form.years_experience, 10) || 0,
        }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.detail || "Application failed");
      setSuccess(true);
      toast.success("Application received. We'll be in touch within 3 business days.");
    } catch (err) {
      toast.error(err.message || "Network error");
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center px-4">
        <div className="max-w-md text-center" data-testid="experts-apply-success">
          <div className="border border-[#00E676]/40 bg-[#00E676]/10 p-3 inline-flex items-center justify-center mb-5">
            <Check className="w-6 h-6 text-[#00E676]" />
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight leading-tight">
            Application received.
          </h1>
          <p className="text-base text-zinc-400 mt-4 leading-relaxed">
            We review every application personally — usually within 3 business days. Watch your
            inbox at <strong className="text-white">{form.email}</strong>.
          </p>
          <div className="mt-8 flex items-center justify-center gap-3 flex-wrap">
            <Link
              to="/"
              className="border border-zinc-800 hover:border-zinc-700 px-5 py-3 font-mono text-[11px] uppercase tracking-widest text-zinc-300 inline-flex items-center gap-2"
            >
              <ChevronLeft className="w-3 h-3" />
              BACK TO HOMEPAGE
            </Link>
            <Link
              to="/experts"
              className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3 hover:opacity-90 inline-flex items-center gap-2"
            >
              READ THE PROGRAMME DETAILS
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white" data-testid="experts-apply-page">
      <header className="border-b border-zinc-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/experts" className="flex items-center gap-2">
            <Terminal className="w-5 h-5 text-[#FFB000]" />
            <span className="font-heading font-black tracking-tight text-lg">DARA</span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">EXPERTS</span>
          </Link>
        </div>
      </header>

      <section className="py-10 sm:py-14">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3">
            // EXPERT APPLICATION · 5 MIN
          </div>
          <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight leading-tight">
            Apply to become a DARA Expert.
          </h1>
          <p className="text-sm text-zinc-400 mt-3 leading-relaxed">
            5 minutes. We review every application personally and respond within 3 business days.
          </p>

          <form
            onSubmit={submit}
            data-testid="experts-apply-form"
            className="mt-8 border border-zinc-800 bg-zinc-950/40 p-6 space-y-4"
          >
            <Field label="FULL NAME *">
              <input
                required value={form.name}
                onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                data-testid="apply-name"
                placeholder="Aisha Patel"
                className={inputCls}
              />
            </Field>
            <Field label="WORK EMAIL *">
              <input
                type="email" required value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                data-testid="apply-email"
                placeholder="aisha@yourdomain.com"
                className={inputCls}
              />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="COUNTRY *">
                <select
                  required value={form.country}
                  onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                  data-testid="apply-country"
                  className={inputCls}
                >
                  {COUNTRIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </Field>
              <Field label="TIMEZONE (E.G. ASIA/KOLKATA)">
                <input
                  value={form.timezone}
                  onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}
                  data-testid="apply-timezone"
                  placeholder="Australia/Sydney"
                  className={inputCls}
                />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="YEARS OF EXPERIENCE *">
                <input
                  type="number" min="0" max="60" required value={form.years_experience}
                  onChange={(e) => setForm((f) => ({ ...f, years_experience: e.target.value }))}
                  data-testid="apply-years"
                  placeholder="7"
                  className={inputCls}
                />
              </Field>
              <Field label="DESIRED TIER *">
                <select
                  required value={form.desired_tier}
                  onChange={(e) => setForm((f) => ({ ...f, desired_tier: e.target.value }))}
                  data-testid="apply-tier"
                  className={inputCls}
                >
                  <option value="standard">Standard ($99 / $199 sessions)</option>
                  <option value="senior">Senior ($199 / $349 sessions)</option>
                </select>
              </Field>
            </div>
            <Field label="LINKEDIN URL">
              <input
                value={form.linkedin}
                onChange={(e) => setForm((f) => ({ ...f, linkedin: e.target.value }))}
                data-testid="apply-linkedin"
                placeholder="https://linkedin.com/in/aisha-patel"
                className={inputCls}
              />
            </Field>
            <div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
                INDUSTRIES YOU KNOW WELL (pick any)
              </span>
              <div className="mt-2 flex flex-wrap gap-2" data-testid="apply-industries">
                {INDUSTRIES.map((i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => toggleIndustry(i)}
                    data-testid={`apply-industry-${i.replace(/[^a-zA-Z0-9]/g, "-")}`}
                    className={`px-3 py-1.5 border font-mono text-[10px] uppercase tracking-widest transition-colors ${
                      form.industries.includes(i)
                        ? "border-[#FFB000] bg-[#FFB000]/10 text-[#FFB000]"
                        : "border-zinc-800 text-zinc-400 hover:border-zinc-700"
                    }`}
                  >
                    {form.industries.includes(i) && <Check className="w-3 h-3 inline mr-1" />}
                    {i}
                  </button>
                ))}
              </div>
            </div>
            <Field label="SHORT BIO * (20–2000 chars)">
              <textarea
                required minLength={20} maxLength={2000} rows={5}
                value={form.bio}
                onChange={(e) => setForm((f) => ({ ...f, bio: e.target.value }))}
                data-testid="apply-bio"
                placeholder="Tell us about your work — clients, industries, what makes your analysis useful. 2-3 sentences."
                className={`${inputCls} font-sans resize-none`}
              />
              <div className="font-mono text-[9px] text-zinc-600 mt-1 text-right">
                {form.bio.length} / 2000
              </div>
            </Field>

            <label
              className="flex items-start gap-3 cursor-pointer border border-zinc-800 bg-black p-4 hover:border-zinc-700 transition-colors"
              data-testid="apply-consent-row"
            >
              <input
                type="checkbox"
                checked={form.email_consent}
                onChange={(e) => setForm((f) => ({ ...f, email_consent: e.target.checked }))}
                data-testid="apply-consent-checkbox"
                required
                className="mt-1 accent-[#FFB000] w-4 h-4 flex-shrink-0 cursor-pointer"
              />
              <div className="text-xs text-zinc-300 leading-relaxed">
                <strong className="text-white">
                  I agree to receive application updates, programme news, and earning reports by email.
                </strong>{" "}
                <span className="text-zinc-500">
                  One-click unsubscribe in every email.{" "}
                  <Link to="/consent" target="_blank" className="text-[#FFB000] hover:underline">
                    Email policy
                  </Link>.
                </span>
              </div>
            </label>

            <button
              type="submit"
              disabled={submitting || !form.email_consent}
              data-testid="apply-submit"
              className="w-full bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-widest px-5 py-3.5 hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  SUBMITTING…
                </>
              ) : (
                <>
                  SUBMIT APPLICATION
                  <ArrowRight className="w-3.5 h-3.5" />
                </>
              )}
            </button>

            <div className="font-mono text-[10px] text-zinc-500 text-center pt-2">
              We respond within 3 business days · personal review · no automated rejections
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}

const inputCls =
  "mt-1.5 w-full bg-black border border-zinc-800 px-3 py-2.5 font-mono text-sm text-white focus:outline-none focus:border-[#FFB000]";

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{label}</span>
      {children}
    </label>
  );
}
